import { getRangeByPositions } from '@blocksuite/affine-shared/utils';

import type {
  MultiSelection,
  TableViewAreaSelection,
} from '../../selection.js';

export type DragPreviewRect = {
  height: number;
  left: number;
  top: number;
  width: number;
};

export function getElementVisualScale(
  element: HTMLElement,
  rect: DOMRect
): number {
  const width = element.offsetWidth;
  if (!width || !rect.width) return 1;
  const scale = rect.width / width;
  return Number.isFinite(scale) && scale > 0 ? scale : 1;
}

export function getDragPreviewRect(
  table: HTMLElement,
  startX: number,
  startY: number,
  x: number,
  y: number
): DragPreviewRect {
  const tableRect = table.getBoundingClientRect();
  const scale = getElementVisualScale(table, tableRect);
  return {
    height: Math.abs(y - startY) / scale,
    left: (Math.min(startX, x) - tableRect.left) / scale,
    top: (Math.min(startY, y) - tableRect.top) / scale,
    width: Math.abs(x - startX) / scale,
  };
}

export function getSelectionAnchorPosition(
  selection: TableViewAreaSelection,
  content: HTMLElement | undefined,
  rowOffsets: number[] | undefined,
  columnOffsets: number[]
): { x: number; y: number } | undefined {
  const contentRect = content?.getBoundingClientRect();
  const anchorX = columnOffsets[selection.columnsSelection.start];
  const anchorY = rowOffsets?.[selection.rowsSelection.start];
  if (!content || !contentRect || anchorX == null || anchorY == null) return;
  const scale = getElementVisualScale(content, contentRect);
  return {
    x: contentRect.left + anchorX * scale,
    y: contentRect.top + anchorY * scale,
  };
}

function clampPointerOffset(value: number, positions: number[]) {
  const min = positions[0];
  const max = positions[positions.length - 1];
  if (min == null || max == null) return value;
  return Math.max(min, Math.min(max, value));
}

export function createCellPositionResolver(
  content: HTMLElement | undefined,
  rows: number[] | undefined,
  columns: number[]
):
  | ((
      x1: number,
      x2: number,
      y1: number,
      y2: number
    ) => { row: MultiSelection; column: MultiSelection })
  | undefined {
  const rect = content?.getBoundingClientRect();
  if (!content || !rect || !rows || rows.length < 2 || columns.length < 2) {
    return;
  }
  const scale = getElementVisualScale(content, rect);
  return (x1, x2, y1, y2) => {
    const localX1 = (x1 - rect.left) / scale;
    const localX2 = (x2 - rect.left) / scale;
    const localY1 = (y1 - rect.top) / scale;
    const localY2 = (y2 - rect.top) / scale;
    const [startX, endX] =
      localX1 < localX2 ? [localX1, localX2] : [localX2, localX1];
    const [startY, endY] =
      localY1 < localY2 ? [localY1, localY2] : [localY2, localY1];
    return {
      column: getRangeByPositions(
        columns,
        clampPointerOffset(startX, columns),
        clampPointerOffset(endX, columns)
      ),
      row: getRangeByPositions(
        rows,
        clampPointerOffset(startY, rows),
        clampPointerOffset(endY, rows)
      ),
    };
  };
}
